import { BellOutlined } from "@ant-design/icons"
import { Avatar } from "components/Avatar/Avatar"
import { Button, type ButtonProps } from "components/Button/Button"
import {
	type DefaultOptionType,
	Select,
	type SelectProps,
} from "components/Select/Select"
import { useDisclosure } from "hooks/useDisclosure"
import { useMemo } from "react"
import { cn } from "styles/cn"

export function AvatarSelect({
	value,
	className,
	options = EMPTY_OPTIONS,
	disabled = false,
	maxAvatarCount = 3,
	"aria-label": ariaLabel,
	buttonProps,
	...selectProps
}: Omit<SelectProps<string[], DefaultOptionType>, "value" | "mode"> & {
	value: string[]
	maxAvatarCount?: number
	buttonProps?: ButtonProps
}) {
	const disclosure = useDisclosure()
	const selectedItems = useMemo(
		() =>
			value.map(
				(val) =>
					options.find((opt) => opt?.value === val) ?? {
						label: val,
						value: val,
					},
			),
		[value, options],
	)

	return (
		<div className={cn("relative inline-flex items-center", className)}>
			<Button
				icon={<BellOutlined aria-hidden />}
				{...buttonProps}
				disabled={disabled}
				aria-label={ariaLabel}
				onClick={disclosure.toggle}
			>
				{value.length > 0 && (
					<Avatar.Group
						size={24}
						className={cn(disabled && "opacity-50")}
						max={{
							count: maxAvatarCount,
						}}
					>
						{selectedItems.map((item) => (
							<Avatar key={String(item.value)} className="text-sm!">
								{item.label}
							</Avatar>
						))}
					</Avatar.Group>
				)}
			</Button>

			{disclosure.isOpen && (
				<div className="absolute top-full left-0 mt-1">
					<Select<string[], DefaultOptionType>
						notFoundContent={null}
						placeholder="Search..."
						{...selectProps}
						autoFocus
						defaultOpen
						value={value}
						mode="multiple"
						virtual={false}
						options={options}
						disabled={disabled}
						aria-label={ariaLabel}
						tagRender={() => <></>}
						classNames={{
							...selectProps.classNames,
							root: "min-w-56",
						}}
						showSearch={{
							optionFilterProp: "label",
						}}
						getPopupContainer={(triggerNode) =>
							triggerNode.parentElement || document.body
						}
						onOpenChange={(isOpen) => {
							if (!isOpen) {
								disclosure.close()
							}
						}}
					/>
				</div>
			)}
		</div>
	)
}

const EMPTY_OPTIONS: DefaultOptionType[] = []
